import { useCallback, useEffect, useState } from "react";
import {
  ReactFlow, Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState,
  Panel,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, message, Spin, Tooltip } from "antd";
import {
  SaveOutlined, ArrowLeftOutlined, PlusOutlined,
} from "@ant-design/icons";
import api from "../../../../../core/axios/axiosInstance";
import { updateFlow, createFlow } from "../../../../../core/data/redux/slices/WabaAutomationSlice";

import TriggerNode           from "./nodes/TriggerNode";
import TextNode              from "./nodes/TextNode";
import MediaNode             from "./nodes/MediaNode";
import InteractiveButtonsNode from "./nodes/InteractiveButtonsNode";
import DelayNode             from "./nodes/DelayNode";
import ActionNode            from "./nodes/ActionNode";

const nodeTypes = {
  trigger:            TriggerNode,
  text:               TextNode,
  media:              MediaNode,
  interactiveButtons: InteractiveButtonsNode,
  delay:              DelayNode,
  addToGroup:         (p) => <ActionNode {...p} type="addToGroup" />,
  removeFromGroup:    (p) => <ActionNode {...p} type="removeFromGroup" />,
  updateContact:      (p) => <ActionNode {...p} type="updateContact" />,
  sendEmail:          (p) => <ActionNode {...p} type="sendEmail" />,
  webhook:            (p) => <ActionNode {...p} type="webhook" />,
};

const NODE_PALETTE = {
  Messages: [
    { type: "text",               label: "Text Message",      color: "#10b981" },
    { type: "media",              label: "Media",             color: "#f59e0b" },
    { type: "interactiveButtons", label: "Buttons",           color: "#8b5cf6" },
  ],
  Actions: [
    { type: "delay",              label: "Delay",             color: "#06b6d4" },
    { type: "addToGroup",         label: "Add to Group",      color: "#10b981" },
    { type: "removeFromGroup",    label: "Remove from Group", color: "#ef4444" },
    { type: "updateContact",      label: "Update Contact",    color: "#f59e0b" },
    { type: "sendEmail",          label: "Send Email",        color: "#3b82f6" },
    { type: "webhook",            label: "Webhook",           color: "#8b5cf6" },
  ],
};

let nodeId = 100;
const uid = () => `node_${++nodeId}_${Date.now()}`;

const FlowBuilder = () => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { id }     = useParams(); // undefined = new flow

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowName, setFlowName]          = useState("Untitled Flow");
  const [loading, setLoading]            = useState(false);
  const [saving, setSaving]              = useState(false);

  // Load existing flow
  useEffect(() => {
    if (!id) {
      // New flow — add default trigger node
      setNodes([{
        id: "trigger_1",
        type: "trigger",
        position: { x: 80, y: 200 },
        data: { triggerType: "keyword", matchCriteria: "contains", keyword: "", onChange: makeOnChange("trigger_1") },
      }]);
      return;
    }
    setLoading(true);
    api.get(`/api/waba/automation/flows/${id}`)
      .then((res) => {
        const flow = res.data.data;
        setFlowName(flow.name);
        setNodes(flow.nodes.map((n) => ({
          ...n,
          data: { ...n.data, onChange: makeOnChange(n.id) },
        })));
        setEdges(flow.edges);
      })
      .catch(() => message.error("Failed to load flow"))
      .finally(() => setLoading(false));
  }, [id]);

  const makeOnChange = (nodeId) => (newData) => {
    setNodes((nds) => nds.map((n) => n.id === nodeId ? { ...n, data: { ...newData, onChange: makeOnChange(nodeId) } } : n));
  };

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({
      ...params,
      animated: true,
      style: { stroke: "#6366f1" },
      label: params.sourceHandle ? undefined : undefined,
    }, eds));
  }, []);

  const addNode = (type) => {
    const newId = uid();
    const newNode = {
      id: newId,
      type,
      position: { x: Math.random() * 300 + 300, y: Math.random() * 300 + 100 },
      data: { onChange: makeOnChange(newId) },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanNodes = nodes.map(({ data, ...rest }) => {
        const { onChange, ...cleanData } = data;
        return { ...rest, data: cleanData };
      });

      const payload = { name: flowName, nodes: cleanNodes, edges };

      if (id) {
        await dispatch(updateFlow({ id, ...payload })).unwrap();
      } else {
        const result = await dispatch(createFlow(payload)).unwrap();
        navigate(`/whatsapp/automation/flows/${result._id}`, { replace: true });
      }
      message.success("Flow saved");
    } catch (err) {
      message.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-wrapper"><div className="content" style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spin /></div></div>;

  return (
    <div className="page-wrapper" style={{ paddingBottom: 0 }}>
      <div className="content" style={{ padding: 0, height: "calc(100vh - 60px)", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
        borderBottom: "1px solid #e5e7eb", background: "#fff",
      }}>
        <Button icon={<ArrowLeftOutlined />} size="small" onClick={() => navigate(-1)}>Back</Button>
        <Input
          value={flowName}
          onChange={(e) => setFlowName(e.target.value)}
          style={{ maxWidth: 280, fontWeight: 600 }}
          size="small"
          bordered={false}
        />
        <div style={{ marginLeft: "auto" }}>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
            Save Flow
          </Button>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex" }}>
        {/* Node Palette */}
        <div style={{
          width: 180, borderRight: "1px solid #e5e7eb", background: "#f9fafb",
          padding: 12, overflowY: "auto",
        }}>
          {Object.entries(NODE_PALETTE).map(([section, items]) => (
            <div key={section} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {section}
              </div>
              {items.map((item) => (
                <div
                  key={item.type}
                  onClick={() => addNode(item.type)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 10px", borderRadius: 7, marginBottom: 5,
                    border: `1px solid ${item.color}22`,
                    background: `${item.color}11`,
                    cursor: "pointer", fontSize: 12, fontWeight: 500,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = `${item.color}22`}
                  onMouseLeave={(e) => e.currentTarget.style.background = `${item.color}11`}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0 }} />
                  {item.label}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Canvas */}
        <div style={{ flex: 1 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
            deleteKeyCode="Delete"
          >
            <Background />
            <Controls />
            <MiniMap />
            <Panel position="bottom-center">
              <div style={{ fontSize: 11, color: "#9ca3af", background: "rgba(255,255,255,0.9)", padding: "4px 10px", borderRadius: 20 }}>
                Click a node type on the left to add • Drag to connect nodes • Delete key to remove selected
              </div>
            </Panel>
          </ReactFlow>
        </div>
      </div>
      </div>
    </div>
  );
};

export default FlowBuilder;
